import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';

/**
 * The vocabulary the admin user page reads its data with — pure, no React.
 *
 * The page used to hold all of this inline: which permission sections are
 * hidden, which are law-firm-only, how a section name becomes a heading, how
 * add-ons get deduplicated. Every one of those rules is a decision about what
 * an admin is shown, so they live here where a test can hold them still.
 */

export interface AdminUser {
  id: number;
  uuid?: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  is_org_admin: boolean;
  is_active: boolean;
  created_at: string;
}

export interface UserPermission {
  id: number;
  name: string;
  description?: string;
  source: 'direct' | 'role' | 'admin';
}

export interface Permission {
  id: number;
  name: string;
  description?: string;
}

export interface PermissionSection {
  id: number;
  name: string;
  permissions: Permission[];
}

export const TYPE_BADGE_COLOR: Record<string, BadgeColor> = {
  base: 'blue',
  trial: 'yellow',
  pilot: 'purple',
  addon: 'gray',
};

/** Permission sections that are not exposed in the UI yet. */
export const HIDDEN_PERMISSION_SECTIONS = [
  'client_pipeline', 'review', 'finance', 'messages', 'dashboard', 'analytics',
];

/** Sections that only make sense to an organization that has clients. */
const CLIENT_SPECIFIC_SECTIONS = ['lawyers', 'finder', 'finances'];
const NON_CLIENT_ORG_TYPES = ['law_firm', 'solo_lawyer', 'in_house'];

/** "data_sources" → "Data Sources". */
export const capitalizeSection = (name: string): string =>
  (name || '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/** The permission sections this organization type should actually see. */
export const visiblePermissionSections = (
  sections: PermissionSection[],
  orgType?: string | null
): PermissionSection[] => {
  const shouldFilter = NON_CLIENT_ORG_TYPES.includes((orgType || '').toLowerCase());
  return (sections || [])
    .filter((s) => !HIDDEN_PERMISSION_SECTIONS.includes((s.name || '').toLowerCase()))
    .filter((s) => !shouldFilter || !CLIENT_SPECIFIC_SECTIONS.includes((s.name || '').toLowerCase()));
};

/** The user's own permissions, minus the ones nothing in the UI can explain. */
export const visibleUserPermissions = (permissions: UserPermission[]): UserPermission[] =>
  (permissions || []).filter(
    (p) => !HIDDEN_PERMISSION_SECTIONS.includes((p.name || '').split(':')[0])
  );

/**
 * The person a `/settings/admin-users/:detailId` URL names.
 *
 * The segment is a uuid for every link the app writes today, and a numeric id
 * for every bookmark and email written before it was — so BOTH resolve here,
 * and neither the route nor the caller has to know which it is holding.
 *
 * `/admin/users` carries no uuid (see `peopleDirectory`), so a uuid is turned
 * into a numeric id through the directory first, and only then looked up. A
 * record that grows a `uuid` of its own is matched directly, so the day the
 * endpoint starts sending one this keeps working with no directory at all.
 */
export const findAdminUser = (
  people: AdminUser[] | null | undefined,
  routeId: string | null | undefined,
  userIdByUuid?: ReadonlyMap<string, number> | null
): AdminUser | null => {
  const key = (routeId || '').trim();
  if (!key || !people) return null;

  const lower = key.toLowerCase();
  const direct = people.find(
    (person) => String(person.id) === key || (person.uuid || '').toLowerCase() === lower
  );
  if (direct) return direct;

  const mapped = userIdByUuid?.get(lower);
  if (mapped == null) return null;
  return people.find((person) => person.id === mapped) || null;
};

/**
 * What a link to this person should put in the URL: their uuid whenever one is
 * known, their numeric id only when none is. Never a row number by choice.
 */
export const adminUserRouteId = (
  user: { id: number; uuid?: string },
  uuidByUserId?: ReadonlyMap<number, string> | null
): string => (user.uuid || uuidByUserId?.get(user.id) || String(user.id));

/**
 * The ids the PUT body carries: everything granted DIRECTLY to this person.
 *
 * Role-granted permissions are deliberately left out. The endpoint replaces the
 * direct set wholesale, so including an inherited one would turn it into a
 * direct grant that survives the role being changed — the exact drift the
 * locked toggles in the grid exist to prevent.
 */
export const directPermissionIds = (permissions: UserPermission[] | null | undefined): number[] =>
  Array.from(
    new Set((permissions || []).filter((p) => p.source !== 'role').map((p) => p.id))
  );

/** The distinct section names a set of granted permissions covers. */
export const permissionSectionNames = (permissions: UserPermission[]): string[] =>
  Array.from(new Set((permissions || []).map((p) => (p.name || '').split(':')[0]).filter(Boolean)));

/**
 * Add-ons arrive once per assignment, so the same product can appear several
 * times. Show each product once — a list that repeats "Research" three times
 * reads as three purchases.
 */
export const dedupeByLicenseId = <T extends { license_id: number }>(items: T[] | null | undefined): T[] => {
  const seen = new Set<number>();
  return (items || []).filter((item) => {
    if (seen.has(item.license_id)) return false;
    seen.add(item.license_id);
    return true;
  });
};

/** A plan's nominal seat count, rendered the way the licence panel shows it. */
export const formatSeatCount = (seatCount: number): string =>
  seatCount >= 500 ? '∞' : String(seatCount);
